package features.teams.ui

import kotlinx.browser.document
import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.js.onClickFunction
import org.w3c.dom.HTMLElement
import features.teams.domain.Team
import AppState
import features.teams.handlers.initTabFromHash
import features.teams.handlers.unarchiveTeam
import Router

object TeamDetailPage {
    fun render(root: HTMLElement) {
        // Restore tab from URL hash if present
        val hash = kotlinx.browser.window.location.hash
        if (hash.isNotEmpty() && hash.startsWith("#")) {
            val tabFromUrl = hash.substring(1) // Remove the # symbol
            val validTabs = listOf("members", "shifts", "schedule", "attendance", "overtime", "timeoff", "settings")
            if (tabFromUrl in validTabs) {
                AppState.currentTab = tabFromUrl
            }
        }
        
        val team = AppState.currentTeam ?: return
        
        // Initialize tab from URL hash if present
        initTabFromHash()
        
        root.append {
            div {
                // Navbar with back button
                shared.ui.components.Navbar.render(this, team.name, showBackButton = true)
                
                // Archived Team Banner
                if (team.isArchived) {
                    div("bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 max-w-7xl mx-auto mt-4 mx-4") {
                        div("flex items-center gap-3") {
                            span("text-2xl") { +"📦" }
                            div("flex-1") {
                                h3("text-lg font-bold text-orange-400") { +"This team is archived" }
                                p("text-sm text-slate-400") { +"No changes can be made to this team" }
                            }
                            if (team.role == "owner") {
                                button(classes = "px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition") {
                                    onClickFunction = { unarchiveTeam(team.id) }
                                    +"Unarchive Team"
                                }
                            }
                        }
                    }
                }
                
                // Team Detail Content
                div("max-w-7xl mx-auto px-4 py-8") {
                    // Tabs (Desktop Only)
                    div("hidden md:flex flex-nowrap gap-2 mb-8 overflow-x-auto pb-2 border-b border-dark-700") {
                        shared.ui.components.Tab.render(this, "members", "Members", hasSoonBadge = false)
                        shared.ui.components.Tab.render(this, "shifts", "Shift Templates", hasSoonBadge = false)
                        shared.ui.components.Tab.render(this, "schedule", "Schedule", hasSoonBadge = false)
                        shared.ui.components.Tab.render(this, "attendance", "Attendance")
                        shared.ui.components.Tab.render(this, "overtime", "Overtime")
                        shared.ui.components.Tab.render(this, "timeoff", "Time Off")
                        if (team.role == "owner") {
                            shared.ui.components.Tab.render(this, "settings", "Settings", hasSoonBadge = false)
                        }
                    }

                    // Mobile Floating Nav (Minimalist Glass)
                    div("md:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full px-4 flex justify-center") {
                        div("bg-dark-900/80 backdrop-blur-md border border-white/10 rounded-full px-6 py-3 flex items-center justify-between gap-6 shadow-2xl max-w-full overflow-x-auto no-scrollbar") {
                            
                            fun DIV.mobileTab(id: String, iconPath: String) {
                                val isActive = AppState.currentTab == id
                                val iconColor = if (isActive) "text-brand-cyan" else "text-slate-400 hover:text-white"
                                
                                button(classes = "$iconColor transition relative flex-shrink-0") {
                                    onClickFunction = {
                                        AppState.currentTab = id
                                        kotlinx.browser.window.location.hash = id
                                        Router.render()
                                    }
                                    svg(classes = "w-6 h-6") {
                                        attributes["viewBox"] = "0 0 24 24"
                                        attributes["fill"] = "none"
                                        attributes["stroke"] = "currentColor"
                                        attributes["stroke-width"] = "2"
                                        attributes["stroke-linecap"] = "round"
                                        attributes["stroke-linejoin"] = "round"
                                        unsafe { +iconPath }
                                    }
                                    if (isActive) {
                                        span("absolute -bottom-2 left-1/2 -translate-x-1/2 w-1 h-1 bg-brand-cyan rounded-full") {}
                                    }
                                }
                            }

                            // Members (Users)
                            mobileTab("members", "<path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2'></path><circle cx='9' cy='7' r='4'></circle><path d='M23 21v-2a4 4 0 0 0-3-3.87'></path><path d='M16 3.13a4 4 0 0 1 0 7.75'></path>")
                            
                            // Shifts (List/Layers)
                            mobileTab("shifts", "<path d='M3 6h18M3 12h18M3 18h18'></path>") // Menu/List icon for templates
                            
                            // Schedule (Calendar)
                            mobileTab("schedule", "<rect x='3' y='4' width='18' height='18' rx='2' ry='2'></rect><line x1='16' y1='2' x2='16' y2='6'></line><line x1='8' y1='2' x2='8' y2='6'></line><line x1='3' y1='10' x2='21' y2='10'></line>")
                            
                            // Attendance (Check Circle)
                            mobileTab("attendance", "<path d='M22 11.08V12a10 10 0 1 1-5.93-9.14'></path><polyline points='22 4 12 14.01 9 11.01'></polyline>")
                            
                            // Overtime (Clock +)
                            mobileTab("overtime", "<circle cx='12' cy='12' r='10'></circle><polyline points='12 6 12 12 16 14'></polyline>")
                            
                            // Time Off (Sun/Umbrella/Coffee) - Using Coffee/Cup
                            mobileTab("timeoff", "<path d='M18 8h1a4 4 0 0 1 0 8h-1'></path><path d='M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z'></path><line x1='6' y1='1' x2='6' y2='4'></line><line x1='10' y1='1' x2='10' y2='4'></line><line x1='14' y1='1' x2='14' y2='4'></line>")
                            
                            // Settings (Cog) - Owner only
                            if (team.role == "owner") {
                                mobileTab("settings", "<circle cx='12' cy='12' r='3'></circle><path d='M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z'></path>")
                            }
                        }
                    }
                    
                    // Tab Content
                    when (AppState.currentTab) {
                        "members" -> features.members.ui.MembersTab.render(this, team)
                        "shifts" -> features.shifts.ui.ShiftsTab.render(this, team)
                        "schedule" -> features.schedule.ui.ScheduleTab.render(this, team)
                        "attendance" -> features.attendance.ui.AttendanceTab.render(this, team)
                        "overtime" -> features.overtime.ui.OvertimeTab.render(this, team)
                        "timeoff" -> features.timeoff.ui.TimeOffTab.render(this, team)
                        "settings" -> features.teams.ui.SettingsTab.render(this, team)
                    }
                }
            }
        }
    }
}
